import DOMPurify from "dompurify";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useImageUtils } from "hooks";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
const defaultSEOTags = {
  title: "Verizon Business: Plans, Services & Solutions | Verizon",
  type: "product",
  keywords:
    "verizon business, verizon small business, verizon medium business, local business",
  description:
    "Explore Verizon's full selection small and medium business solutions including FiOS, high speed internet, phone, and TV service."
};

const SEOTagger = ({ pageName, pdpMeta, currentFlow, isDigitalQuote }) => {
  const [title, setTitle] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState(
    window.document.location.href
  );
  const [isCanonicalUrlExist, isCanonicalExist] = useState(false);
  const { t, ready } = useTranslation(["app"], { useSuspense: false });
  const aemContent = ready ? t("app", { ns: "app", returnObjects: true }) : {};
  const [imageFetched, setImageFetched] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [metaTags, setMetaTags] = useState([]);
  const metaInformation = useSelector(state => state.GlobalData?.metaInformation);
  const showUpdatedFavIcon = t("showUpdatedFavIcon", { ns: "app" });
  const learnPlanCode =
    useSelector((state) => state.GlobalData.queryParams?.pricePlanCode);
  const learnBackupPlanCode =
    useSelector((state) => state.GlobalData.queryParams?.backupPricePlanCode);

  const { getCarouselImagesetData } = useImageUtils();
  const icon = (showUpdatedFavIcon === true) ? (window?.mbtGlobal?.faviconUrl ? window?.mbtGlobal?.faviconUrl
  : window?.mbtGlobal?.assetsBaseUrl + "/assets/images/Verizon_glow_favicon.ico") : window?.mbtGlobal?.assetsBaseUrl + "/assets/images/vz-favicon-circle.ico";
  const getTitleAndDesc = (isNoMetaInfo = false) => {
    const headings = metaInformation?.headings;
    if (pageName && isNoMetaInfo) {
      return headings?.[pageName]?.default;
    }
  }

  useEffect(() => {
    // parse SEO data from metaInformation API response
    if (metaInformation?.meta) {
      let seoDataForPage = metaInformation?.meta[pageName];
      if (pdpMeta) {
        seoDataForPage = pdpMeta;
        seoDataForPage.isProduct = true;
      }
      if (!seoDataForPage) {
        let tempTag = { ...defaultSEOTags };
        const defaultHeadings = getTitleAndDesc(true);
        if (defaultHeadings?.title) {
          tempTag.title = `Verizon Business | ${defaultHeadings?.title}`;
        }
        if (defaultHeadings?.description) {
          tempTag.description = defaultHeadings?.description;
        }
        if (defaultHeadings?.keywords) {
          tempTag.keywords = defaultHeadings?.keywords;
        }
        seoDataForPage = tempTag;
      }
      setTags(seoDataForPage);
    }
  }, [pdpMeta, pageName, metaInformation, window?.location?.href, ready]);

  const setTags = (config) => {
    const tags = [];

    if (pdpMeta) {
      const cpoText = config.isCPO ? CPO + " " : "";
      if (config?.brandName && config?.title && config?.name) {
        config.title = `${cpoText}${config.brandName} ${config.title}`;
        config.name = `${cpoText}${config.brandName} ${config.name}`;
      }

      if (!imageUrl && config?.image && !imageFetched) {
        setImageFetched(true);
        getCarouselImagesetData(config.image + "?req=set,json").then(
          (i) => setImageUrl(i[0])
        );
      }
    }

    tags.push({ property: "og:site_name", content: "VerizonBusiness" });
    tags.push({ name: "twitter:card", content: "summary_large_image" });
    tags.push({ name: "twitter:site", content: "@VerizonBusiness" });

    // if (window?.location?.href?.indexOf("/shop/products/devices/") > -1) {
    //   tags.push({ name: "robots", content: "index" });
    //   // tags.push({ name: "googlebot", content: "noindex" });
    // }
    // else if (window?.location?.href?.indexOf("/products/devices/") > -1) {
    //   tags.push({ name: "robots", content: "index" });
    // }

    if (config.title) {
      let seoTitle = false;
      let shopUrl = false;
      let newSeoTitle = '';
      let newSeoDescription = '';
      let newSeoKeywords = '';
      const newTitle = getTitle(config.title, config.titleSuffix);
      if (aemContent?.enableSeoMapping && aemContent?.seoElementData) {
        Object.keys(aemContent?.seoElementData).forEach((key) => {
          if (window?.location?.href?.indexOf(key) > -1 &&
            (window?.location?.href?.lastIndexOf(key) + key?.length) === window?.location?.href?.length) {
            seoTitle = true;
            newSeoTitle = aemContent?.seoElementData[key]?.page_title;
            newSeoDescription = aemContent?.seoElementData[key]?.meta_description;
            newSeoKeywords = aemContent?.seoElementData[key]?.meta_keywords;
          }
        });
      }
      if (aemContent?.shopSeoElements) {
        aemContent?.shopSeoElements?.forEach(key => {
          if (window?.location?.href?.indexOf(key) > -1
            && (window?.location?.href?.lastIndexOf(key) + key?.length) === window?.location?.href?.length) {
            shopUrl = true;
          }
        })
      }
      if (!seoTitle) {
        setTitle(newTitle);
        if (config.description) {
          tags.push(
            ...getDescriptionTags(config.description, config.skipEquipText)
          );
          if (shopUrl) {
            setRobotTag("noindex, nofollow");
          } else {
            setRobotTag("index");
          }
          if (!pdpMeta) {
            removeDescription();
          } else {
            // setDescription("Equip your business. " + config.description);
            setDescription(config.description);
          }
          removeKeywords();
        }
      } else {
        setTitle(newSeoTitle);
        setRobotTag("index");
        setDescription(newSeoDescription);
        setKeyWords(newSeoKeywords);
      }
      // removeExistingCanonical()
      tags.push({
        property: "og:title",
        content: newTitle.split(" | ")[0] ? newTitle.split(" | ")[0] : newTitle,
      });
      tags.push({
        name: "twitter:title",
        content: newTitle.split(" | ")[0] ? newTitle.split(" | ")[0] : newTitle,
      });
    }
    if (config.name) {
      tags.push({ name: "name", content: config.name });
    }

    const type = config.isProduct ? "product" : "website";
    tags.push({ property: "og:type", content: type });

    if (config.url) {
      tags.push({ property: "og:url", content: `${config.url}` });
    } else {
      let href = window.document.location.href;
      if (pageName === "4g" || pageName === "5g") {
        if (learnBackupPlanCode) {
          href = href + `?backupPricePlanCode=${learnBackupPlanCode}`;
        } else if (learnPlanCode) {
          href = href + `?pricePlanCode=${learnPlanCode}`;
        }
      }
      tags.push({
        property: "og:url",
        content: `${href}${pdpMeta ? "/" : ""}`,
      });
    }
    addCanonicalURL();
    setMetaTags(tags);
  };
  const setDescription = (desc) => {
    let description = desc.trim().slice(0,320);
    const el = window.document?.querySelector("meta[name='description']");
    el?.setAttribute('content', description)
  }
  const setRobotTag = (desc) => {
    const el = window.document?.querySelector("meta[name='robots']");
    el?.setAttribute('content', desc)
  }
  const setKeyWords = (desc) => {
    const el = window.document?.querySelector("meta[name='keywords']");
    el?.setAttribute('content', desc)
  }
  const setExistingCanonical = (url) => {
    const el = window.document?.querySelector("link[rel='canonical']");
    el?.setAttribute('href', url)
  }
  const checkCanonical = (url) => {
    const el = window.document?.querySelector("link[rel='canonical']");
    if (el && el !== null && el !== undefined) {
      return true;
    } else {
      return false;
    }
  }
  const removeDescription = () => {
    const el = window.document.querySelector("meta[name='description']");
    el?.remove();
  }
  const removeKeywords = () => {
    const el = window.document.querySelector("meta[name='keywords']");
    el?.remove();
  }
  const stripTrailingSlash = (str) => {
    return str.endsWith('/') ?
      str.slice(0, -1) :
      str;
  };
  const addCanonicalURL = () => {
    const fullURL = window?.location?.href?.toLowerCase();
    let urlWithoutParams = fullURL.split("?")[0];
    urlWithoutParams = urlWithoutParams.split("#")[0];
    const canonicalUrl = stripTrailingSlash(urlWithoutParams)
    const isCanExist = checkCanonical(canonicalUrl);
    isCanonicalExist(isCanExist);
    setCanonicalUrl(canonicalUrl);
    setExistingCanonical(canonicalUrl);
  };

  const getTitle = (title, titleSuffix) => {
    title = makeTitle(title);
    let contentDiv = window.document.createElement("div");
    contentDiv.innerHTML = title;
    title = contentDiv.textContent;
    return decodeURIComponent(title);
  };

  const getDescriptionTags = (content, skipEquipText = false) => {
    let contentDiv = window.document.createElement("div");
    contentDiv.innerHTML = content;
    let textContent = contentDiv.textContent;
    // if (textContent.indexOf("Equip your business.") === -1 && !skipEquipText) {
    //   textContent = "Equip your business. " + textContent;
    // }
    return [
      { name: "description", content: textContent },
      { property: "og:description", content: textContent },
      { name: "twitter:description", content: textContent },
    ];
  };

  const makeTitle = (str) => {
    return str.replace(/(&.+;)/gi, "").replace(/-+/g, "-");
  };

  if (!ready) {
    return <></>
  }
  return (
    <Helmet>
      <title>{title}</title>
      {icon && <link rel="icon" href={icon} type="image/x-icon" />}
      {!isCanonicalUrlExist && <link rel="canonical" href={DOMPurify.sanitize(canonicalUrl)} />}
      {metaTags.map((tag) => {
        if (tag?.name) {
          return (
            <meta
              key={tag?.name}
              name={tag?.name ?? ""}
              content={tag?.content ?? ""}
            />
          );
        } else {
          return (
            <meta
              key={tag?.property}
              property={tag?.property ?? ""}
              content={tag?.content ?? ""}
            />
          );
        }
      })}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
    </Helmet>
  );
};

export default SEOTagger;
